'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Message, useToaster, Input, Checkbox, Button, InputGroup } from 'rsuite'
import { Eye, EyeOff, ArrowRight, Lock, Package, FileText, Store, EyeClosed, EyeIcon, ArrowRightIcon } from 'lucide-react'
import mainLogo from '../../../assets/images/items/Growwiz-main-logo.png';
import mainLogoWhite from '../../../assets/images/items/Growwiz logo white.png';
import { getDeviceId, getReadableDeviceName } from '../../../utils/fingerPrint'
import globalIP from '../../../GlobelIP'
import ugr from '../../../UGRoute'
import { getDeviceId as getTDDeviceId } from "@trustdevicejs/trustdevice-js";

export default function CustomerLoginPage() {
  const navigate = useNavigate()
  const toaster = useToaster()

  // UI States
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const [loginError, setLoginError] = useState('')

  const currentYear = new Date().getFullYear()

  // Convert email → username (backend requirement)
  const username = email

  // Old Logic Functions
  const fetchUserData = async (id) => {
    try {
      const response = await fetch(`${globalIP}/api/user-details/${id}`)
      const json = await response.json()
      return json.data.attributes
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  const isYesterday = (date) =>
    new Date(date).setHours(0, 0, 0, 0) <
    new Date().setHours(0, 0, 0, 0)
  function generateUUID() {
    // universal fallback
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getStableDeviceId() {
    let id = localStorage.getItem("deviceId");

    if (!id) {
      const cookieMatch = document.cookie.match(/deviceId=([^;]+)/);
      if (cookieMatch) {
        id = cookieMatch[1];
        localStorage.setItem("deviceId", id);
      }
    }

    if (!id) {
      if (window.crypto && window.crypto.randomUUID) {
        id = "DEV-" + window.crypto.randomUUID();
      } else {
        id = "DEV-" + generateUUID(); // fallback
      }

      localStorage.setItem("deviceId", id);
      document.cookie = `deviceId=${id}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
    }

    return id;
  }


  const handleSignIn = async () => {
    setLoginError('');
    // const deviceId = await getDeviceId();
    const deviceId = getStableDeviceId();
    const deviceLabel = `${username}-${await getReadableDeviceName()}`;

    const today = new Date();

    try {
      // 1️⃣ Call login-with-device API
      const response = await fetch(`${globalIP}/api/auth/login-with-device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: username, password, deviceId, deviceLabel })
      });

      const data = await response.json();
      if (!response.ok) {
        // Show toast if API returns error
        const errorMessage = data?.error?.message || 'Device login failed';
        toaster.push(
          <Message showIcon type="error">
            {errorMessage}
          </Message>,
          { placement: 'topCenter' }
        );
        setLoginError(errorMessage);
        return;
      }
      if (data.status === "PENDING_APPROVAL") {
        toaster.push(
          <Message showIcon type="warning">
            Your device is not approved yet. Please wait for admin approval.
          </Message>,
          { placement: 'topCenter' }
        );
        setLoginError("Device login pending approval.");
        return; // Stop login flow
      }
      const { jwt, user } = data; // <-- New API response

      // 2️⃣ LogT / LogoutT logic
      if (user.LogT === 0 || user.LogT === null || isYesterday(user.LogoutT)) {
        localStorage.setItem('LoginCode', '1');

        await fetch(`${globalIP}/api/user-details/${user.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { LogT: '1', LogoutT: new Date().toISOString() }
          })
        });
      } else if (!user.isUserDiscontinue && user.LogT > 0) {
        const continueLogin = window.confirm(
          'This user account is logged in elsewhere. Log out from the other session and continue?'
        );

        if (!continueLogin) return;

        const newLogT = Number(user.LogT) + 1;
        localStorage.setItem('LoginCode', `${newLogT}`);

        await fetch(`${globalIP}/api/user-details/${user.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: { LogT: `${newLogT}`, LogoutT: new Date().toISOString() }
          })
        });
      }

      // 3️⃣ Company expiry check
      const companyResponse = await fetch(`${globalIP}/api/company-details`);
      const companyData = await companyResponse.json();

      if (!companyResponse.ok || !companyData.data || companyData.data.length === 0) {
        setLoginError('Company data not found.');
        return;
      }

      const company = companyData.data[0];
      const expiryDate = new Date(company.attributes.ExpiryDt);

      if (today >= expiryDate) {
        alert('Login not allowed. Your company subscription has expired.');
        setLoginError('Your company subscription has expired.');
        return;
      }

      // 4️⃣ Fetch complete user data (optional if needed)
      const udata = await fetchUserData(user.id);

      if (udata && udata.isUserDiscontinue) {
        toaster.push(
          <Message showIcon type="error">User not found. Please reactivate the user first.</Message>,
          { placement: 'topCenter' }
        );
        return;
      }

      // 5️⃣ Save session data
      localStorage.setItem('jwt', jwt);
      localStorage.setItem('id', user.id);
      localStorage.setItem('udata', JSON.stringify(udata));
      localStorage.setItem('fullName', JSON.stringify(user.Full_Name));
      localStorage.setItem('useid', JSON.stringify(user.Username));
      sessionStorage.setItem('fullName', JSON.stringify(user.Full_Name));
      sessionStorage.setItem('useid', JSON.stringify(user.Username));
      localStorage.setItem('key', 'main');

      // Set session expiry at end of day
      const date = new Date();
      date.setHours(23, 59, 59, 999);
      localStorage.setItem('session_time', JSON.stringify(date));

      // 6️⃣ Remember me
      if (rememberMe) {
        localStorage.setItem('rememberMeStatus', 'true');
        localStorage.setItem('rememberMeUsername', username);
      } else {
        localStorage.removeItem('rememberMeStatus');
        localStorage.removeItem('rememberMeUsername');
      }

      // 7️⃣ Success toast & redirect
      toaster.push(<Message showIcon type="success">Login Successfully.</Message>, { placement: 'topEnd' });
      navigate(`/${ugr}/dashboard`);

    } catch (error) {
      console.error('Login error:', error);
      setLoginError('An error occurred during login.');
    }
  };



  const handleSignIn1 = async () => {
    setLoginError('')
    const deviceId = await getDeviceId();
    const today = new Date()

    // API endpoint for user details
    const userDetailsUrl = `${globalIP}/api/user-details?populate=*&filters[Username][$eq]=${encodeURIComponent(
      username
    )}&filters[Password][$eq]=${encodeURIComponent(password)}`

    // API endpoint for company details
    const companyDetailsUrl = `${globalIP}/api/company-details`

    try {
      // Fetch user details
      const userResponse = await fetch(userDetailsUrl)
      const userData = await userResponse.json()

      if (userResponse.ok && userData.data && userData.data.length > 0) {
        const user = userData.data[0]

        // LogT logic
        if (
          user.attributes.LogT == 0 ||
          user.attributes.LogT == null ||
          isYesterday(user.attributes.LogoutT)
        ) {
          localStorage.setItem('LoginCode', '1')

          await fetch(`${globalIP}/api/user-details/${user.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: {
                LogT: '1',
                LogoutT: new Date().toISOString()
              }
            })
          })
        } else if (!user.attributes.isUserDiscontinue && user.attributes.LogT > 0) {
          const continueLogin = window.confirm(
            'This user account is logged in elsewhere. Log out from the other session and continue?'
          )

          if (continueLogin) {
            localStorage.setItem('LoginCode', `${Number(user.attributes.LogT) + 1}`)

            await fetch(`${globalIP}/api/user-details/${user.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                data: {
                  LogT: `${Number(user.attributes.LogT) + 1}`,
                  LogoutT: new Date().toISOString()
                }
              })
            })
          } else {
            return
          }
        }

        // Company expiry validation
        const companyResponse = await fetch(companyDetailsUrl)
        const companyData = await companyResponse.json()

        if (companyResponse.ok && companyData.data && companyData.data.length > 0) {
          const company = companyData.data[0]
          const expiryDate = new Date(company.attributes.ExpiryDt)

          if (today < expiryDate) {
            // FINAL LOGIN SUCCESS
            localStorage.setItem('isLoggedIn', 'true')

            const id = user.id
            const fullName = user.attributes.Full_Name
            const userid = user.attributes.Username

            const udata = await fetchUserData(id)

            if (udata && udata?.isUserDiscontinue) {
              toaster.push(
                <Message showIcon type="error">User not found. Please reactivate the user first.</Message>,
                { placement: 'topCenter' }
              )
              return
            }

            localStorage.setItem('id', id)
            localStorage.setItem('udata', JSON.stringify(udata))
            sessionStorage.setItem('fullName', JSON.stringify(fullName))
            sessionStorage.setItem('useid', JSON.stringify(userid))
            localStorage.setItem('key', 'main')

            localStorage.setItem('fullName', JSON.stringify(fullName))
            localStorage.setItem('useid', JSON.stringify(userid))

            let date = new Date()
            date.setHours(23)
            date.setMinutes(59)
            date.setSeconds(59)
            localStorage.setItem('session_time', JSON.stringify(date))

            // Remember me logic
            if (rememberMe) {
              localStorage.setItem('rememberMeStatus', 'true')
              localStorage.setItem('rememberMeUsername', username)
            } else {
              localStorage.removeItem('rememberMeStatus')
              localStorage.removeItem('rememberMeUsername')
            }

            // Success toast
            toaster.push(
              <Message showIcon type="success">Login Successfully.</Message>,
              { placement: 'topEnd' }
            )

            navigate(`/${ugr}/dashboard`)
          } else {
            alert('Login not allowed. Your company subscription has expired.')
            setLoginError('Your company subscription has expired.')
          }
        }
      } else {
        setLoginError('Invalid username or password.')
      }
    } catch (error) {
      console.log('An error occurred during login:', error)
      setLoginError('An error occurred during login.')
    }
  }

  const features = [
    { icon: Package, title: 'Product Information Management (PIM)', desc: 'Centralized product library, variants & specs.' },
    { icon: FileText, title: 'e-Catalog & Sharing', desc: 'Digital catalogs, WhatsApp share, customer views.' },
    { icon: Store, title: 'Quotation Builder', desc: 'Smart quotations, pricing, follow-up tracking.' },
    { icon: Store, title: 'Lead Manager / Trade Fair Mode', desc: 'Offline capture, sync, tagging, customer profiling.' }
  ]

  return (
    <div className="h-screen flex overflow-hidden">
      {/* LEFT PANEL */}
      <div className="hidden lg:flex lg:w-1/2 h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 
        flex-col justify-between px-12 py-10 relative overflow-hidden">

        {/* Background blobs */}
        <div className="absolute top-40 left-1/3 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute -bottom-20 right-1/4 w-full h-96 bg-teal-500/10 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-slate-700/20 rounded-full blur-3xl opacity-30"></div>

        {/* CONTENT WRAPPER */}
        <div className="relative z-10 flex flex-col h-full justify-between items-start text-left w-full">

          {/* --- TOP SECTION --- */}
          <div className="w-full">
            <img src={mainLogoWhite} alt="Main Logo" style={{ width: '40%', height: 'auto' }} />
          </div>

          {/* --- CENTER SECTION --- */}
          <div className="flex flex-col space-y-2 w-full mt-5">
            <h2 className="text-5xl font-bold leading-tight text-left">
              <span className="text-white">One workspace for all your </span>
              <span className="bg-gradient-to-r from-teal-300 to-teal-400 bg-clip-text text-transparent">
                products, catalogs <br /> & sales.
              </span>
            </h2>

            <p className="text-slate-300 text-lg text-left">
              Manage products, create quotations, share e-catalogs, capture leads — everything in one unified platform.
            </p>

            <p className="text-slate-400 text-xs mb-2 text-left w-full mt-3">
              Growwizo is a B2B SaaS product — add credibility:
            </p>

            <ul className="list-none space-y-2 loginDescription" style={{
              display: "flex", alignItems: "flex-start", width: "100%",
              flexDirection: "column",paddingLeft:"0px !important"
            }}>
              <li className="flex gap-2 items-start">
                <ArrowRight size={16} className="mt-1 text-teal-300" />
                <span className="text-slate-400">
                  Used by manufacturers, exporters & brands across India.
                </span>
              </li>
              <li className="flex gap-2 items-start" >
                <ArrowRight size={16} className="mt-1 text-teal-300" />
                <span className="text-slate-400">
                  <span className="text-teal-300">10,000+</span> products organized.
                </span>
              </li>
              <li className="flex gap-2 items-start">
                <ArrowRight size={16} className="mt-1 text-teal-300" />
                <span className="text-slate-400">
                  <span className="text-teal-300">100+</span> catalogs shared daily.
                </span>
              </li>
            </ul>

          </div>

          {/* --- FOOTER SECTION --- */}
          <div className="mt-auto w-full text-left pt-6 border-t border-white/10">
            <p className="text-xs text-slate-500">
              © {currentYear} Growwizo Technologies. All rights reserved.
            </p>
          </div>

        </div>
      </div>




      {/* RIGHT PANEL */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-8 lg:px-16 py-6 bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950" style={{height:"100%"}}>
        <div className="max-w-md mx-auto w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl shadow-black/10 dark:shadow-black/30 border border-slate-200/60 dark:border-slate-700/60 p-10">

          <h2
            style={{
              fontSize: '1.5rem',      // similar to text-2xl
              fontWeight: 700,         // bold
              color: '#000000',        // black
              lineHeight: 1.3,
              textAlign: "left"
            }}
          >
            Welcome to Growwizo
          </h2>
          <p class="fw-semibold mb-2 mt-3 text-left">
            Sign in with your business account.
          </p>

          <div className="mb-3 mt-4 text-left">
            <label htmlFor="email" className="form-label " style={{ fontSize: '0.95rem', color: '#000000' }} // black label
            >
              Username
            </label>
            <Input
              id="email"
              placeholder="Enter your email or mobile"
              value={email}
              onChange={setEmail}
              size="lg"
              className="form-control"
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '0.75rem',
                border: '1px solid #cbd5e1',
                fontSize: '0.95rem'
              }}
            />
          </div>

          <div className="position-relative mb-1 text-left">
            <label htmlFor="password" className="form-label text-left" style={{ fontSize: '0.95rem', color: '#000000' }} // black label
            >
              Password
            </label>
            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={setPassword}
                size="lg"
                style={{
                  borderRadius: '0.75rem',
                  paddingRight: '2.5rem', // ensure space for icon
                }}
              />
              <InputGroup.Addon
                style={{
                  cursor: 'pointer',
                  background: 'transparent',
                  border: 'none',
                  padding: '0 0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeClosed size={20} /> : <EyeIcon size={20} />}
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className='text-left'>
            <Checkbox checked={rememberMe} onChange={(val) => setRememberMe(val)} >Remember me</Checkbox>
          </div>
          {/* {loginError && <p className="text-red-500 text-sm text-center">{loginError}</p>} */}

          <Button
            appearance="primary"
            block
            onClick={handleSignIn}
            style={{
              backgroundColor: 'rgba(4,12,31,1)',
              borderColor: 'rgba(4,12,31,1)',
              color: '#fff',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              borderRadius: '0.75rem',
              padding: '0.75rem 1rem',
              transition: 'all 0.3s ease',  // smooth hover animation
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)', // default subtle shadow
              transform: 'translateY(0)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.2)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Sign in <ArrowRightIcon size={20} />
          </Button>


          {/* <div className="d-flex align-items-center my-3">
            <div className="flex-grow-1 border-top" style={{ borderColor: '#cbd5e1' }}></div>
            <span className="mx-2 text-muted" style={{ fontSize: '0.85rem' }}>OR</span>
            <div className="flex-grow-1 border-top" style={{ borderColor: '#cbd5e1' }}></div>
          </div>
          <Button
            appearance="default"
            block
            onClick={() => alert('Request access functionality is demo only')}
            style={{
              backgroundColor: 'transparent',      // remove background
              color: '#000000',                    // black text
              border: '1px solid grey',        // black border
              borderRadius: '0.75rem',            // rounded corners
              padding: '0.75rem 1rem',
              fontWeight: 600,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'} // hover effect
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            Request business account access
          </Button> */}

          {/* <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <Lock size={16} />
            <span>Your data is encrypted and secured.</span>
          </div> */}
        </div>
      </div>
    </div>
  )
}
